/**
 * Controles sobrepostos ao mapa: camada, cercas, vias ruins e desselecionar.
 *
 * Controle próprio em vez do `L.control.layers` do Leaflet: aquele traz o visual
 * padrão dele, que destoa do resto do painel e não dá para animar. Aqui o
 * componente é React puro posicionado por cima — o mapa não precisa saber que
 * ele existe.
 *
 * ## Só ícone, com o texto no `title`
 *
 * Os botões perderam o rótulo escrito em 28/10. Eles ficam **sobre o mapa**, e
 * cada palavra ali é um pedaço de rua que alguém não vê — numa tela de
 * operação, três botões escritos cobriam um quarteirão. O texto não sumiu: foi
 * para o `title`, que aparece ao passar o mouse, e para o `aria-label`, que é o
 * que o leitor de tela anuncia. Botão só com ícone e sem nome acessível é
 * botão que não existe para quem não enxerga.
 */
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { TILE_LAYERS } from "../lib/tileLayers.ts";
import { useFleetStore } from "../store/fleet.store.ts";
import { useMapStore } from "../store/map.store.ts";

export function MapLayerControl() {
  const tileLayerId = useMapStore((state) => state.tileLayerId);
  const setTileLayer = useMapStore((state) => state.setTileLayer);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fecha ao clicar fora e no Esc — comportamento esperado de qualquer menu.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const current = TILE_LAYERS.find((layer) => layer.id === tileLayerId);

  return (
    // `z-[1000]` porque os painéis do Leaflet vivem em 400–800: abaixo disso o
    // menu abre atrás do mapa. E o `pointer-events` impede que arrastar aqui
    // arraste o mapa junto.
    <div ref={containerRef} className="absolute right-3 top-3 z-[1000] flex flex-col items-end gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="listbox"
        // O nome da camada atual entra no rótulo acessível e na dica: sem ele,
        // o botão só diria "camada" e esconderia qual está no ar.
        aria-label={`Camada do mapa: ${current?.name ?? "padrão"}`}
        title={`Camada do mapa — ${current?.name ?? "padrão"}`}
        className="flex items-center justify-center rounded-lg border border-line bg-panel/90 p-2 text-ink shadow-lg backdrop-blur transition-colors hover:border-muted"
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M10 2 1 6.5 10 11l9-4.5L10 2Z" />
          <path d="m3.6 9.6-2.6 1.3L10 15.4l9-4.5-2.6-1.3L10 12.8 3.6 9.6Z" opacity=".6" />
        </svg>
      </button>

      <GeofenceButton />
      <RoadLayerButton />
      <DeselectButton />

      <AnimatePresence>
        {open ? (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.14 }}
            className="mt-1.5 w-48 overflow-hidden rounded-lg border border-line bg-panel/95 p-1 shadow-xl backdrop-blur"
          >
            {TILE_LAYERS.map((layer) => (
              <li key={layer.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={layer.id === tileLayerId}
                  onClick={() => {
                    setTileLayer(layer.id);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors ${
                    layer.id === tileLayerId ? "bg-accent/15 text-accent" : "text-ink hover:bg-subtle"
                  }`}
                >
                  <span className={`size-2 rounded-full ${layer.dark ? "bg-muted" : "bg-amber-300"}`} />
                  <span className="flex-1">{layer.name}</span>
                  {layer.id === tileLayerId ? <span aria-hidden="true">✓</span> : null}
                </button>
              </li>
            ))}
          </motion.ul>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/**
 * Liga e desliga as cercas. LIGADAS por padrão — saber se o veículo saiu da
 * área é operação, não investigação.
 */
function GeofenceButton() {
  const showGeofences = useMapStore((state) => state.showGeofences);
  const toggleGeofences = useMapStore((state) => state.toggleGeofences);
  return (
    <button
      type="button"
      onClick={toggleGeofences}
      aria-pressed={showGeofences}
      aria-label={showGeofences ? "Cercas: ligadas" : "Cercas: desligadas"}
      title="Cercas — mostra as cadastradas; as inativas aparecem tracejadas"
      className={`flex items-center justify-center rounded-lg border p-2 shadow-lg backdrop-blur transition-colors ${
        showGeofences
          ? "border-violet-500/60 bg-violet-500/15 text-violet-700 dark:text-violet-300"
          : "border-line bg-panel/90 text-muted hover:border-muted"
      }`}
    >
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M10 2.5 17 6v8l-7 3.5L3 14V6l7-3.5Z" />
      </svg>
    </button>
  );
}

/**
 * Liga a camada de via ruim. Desligada por padrão: ela serve para investigar
 * manutenção, não para operar a frota — e ligada o tempo todo polui o mapa.
 */
function RoadLayerButton() {
  const showRoadLayer = useMapStore((state) => state.showRoadLayer);
  const toggleRoadLayer = useMapStore((state) => state.toggleRoadLayer);
  return (
    <button
      type="button"
      onClick={toggleRoadLayer}
      aria-pressed={showRoadLayer}
      aria-label={showRoadLayer ? "Via ruim: ligada" : "Via ruim: desligada"}
      title="Via ruim — marca onde o acelerômetro levou solavanco forte (buraco, lombada)"
      className={`flex items-center justify-center rounded-lg border p-2 shadow-lg backdrop-blur transition-colors ${
        showRoadLayer
          ? "border-orange-500/60 bg-orange-500/15 text-orange-700 dark:text-orange-300"
          : "border-line bg-panel/90 text-muted hover:border-muted"
      }`}
    >
      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M7 2h6l3 16h-4l-.6-4h-2.8L8 18H4L7 2Zm2.1 5-.5 3h2.8l-.5-3H9.1Z" />
      </svg>
    </button>
  );
}

/**
 * Larga o veículo selecionado.
 *
 * **Só existe quando há seleção.** Botão permanentemente desabilitado ocupa
 * espaço do mapa dizendo "não dá" — e aqui espaço do mapa é rua que alguém
 * precisa ver.
 *
 * Existe porque fechar a gaveta de detalhe deixou de desselecionar: o veículo
 * continua destacado e com rastro desenhado depois de fechar, e sem este botão
 * não haveria como voltar à frota inteira sem recarregar a página.
 */
function DeselectButton() {
  const selectedImei = useFleetStore((state) => state.selectedImei);
  const select = useFleetStore((state) => state.select);
  return (
    <AnimatePresence>
      {selectedImei ? (
        <motion.button
          type="button"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.14 }}
          onClick={() => select(null)}
          aria-label="Desselecionar o veículo"
          title="Desselecionar — volta a mostrar a frota inteira, sem rastro"
          className="flex items-center justify-center rounded-lg border border-accent/60 bg-accent/15 p-2 text-accent shadow-lg backdrop-blur transition-colors hover:bg-accent/25"
        >
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          </svg>
        </motion.button>
      ) : null}
    </AnimatePresence>
  );
}
